<template>
  <el-dialog
    top="3vh"
    width="87vw"
    append-to-body
    class="cfr-sample-dialog"
    :title="title"
    :visible="true"
    :close-on-click-modal="false"
    @update:visible="handleClose"
  >
    <div class="dialog-content">
      <div class="cfr-sample-dialog__header">
        <el-steps :space="200" :active="step.active" align-center finish-status="success">
          <el-step :title="step.collSampleBg"></el-step>
          <el-step :title="step.collSample"></el-step>
          <el-step :title="step.samplefilter"></el-step>
        </el-steps>
        <div v-if="timing.visible" class="timing">{{ timing.value }}</div>
        <el-button
          type="primary"
          size="small"
          style="margin-bottom: 20px"
          :loading="sampleLoading"
          :disabled="sampleStatus === -1"
          v-if="sampleStatus !== 5"
          @click="dispatchEvent"
        >
          {{ btnText }}
        </el-button>

        <el-button
          type="primary"
          size="small"
          style="margin-bottom: 20px"
          :loading="resetLoading"
          @click="resetSample"
        >
          {{ $t('重置') }}
        </el-button>

        <el-button
          v-if="isFinished"
          type="primary"
          size="small"
          style="margin-bottom: 20px"
          :loading="loading"
          @click="getData"
        >
          {{ $t('刷新') }}
        </el-button>
      </div>

      <div class="cfr-sample-dialog__main">
        <v-chart
          style="height: 50% !important"
          :options="sensorOptions"
          ref="charts1"
          :autoresize="true"
        />

        <v-chart
          style="height: 50% !important"
          :options="channelOptions"
          ref="charts2"
          :autoresize="true"
          @datazoom="handleZoom"
        />
      </div>
    </div>
  </el-dialog>
</template>

<script>
import {
  resetFcbrSampleById,
  getFcbrSampleData,
  getFcbrSampleStatus,
  saveFcbrMatLibFilter,
  setFcbrSample
} from '@/api/material';

import { awaitTo } from '@/utils/utils';
import {
  cfrBtnUtils,
  parseChartData,
  makeChannelOpts,
  makeSensorOpts,
  parseRealTimeChartData,
  sampleMap
} from './utils';
import { isEmpty, isFunction, isNumber } from '@/utils/is';

export default {
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    sn: {
      type: String,
      default: ''
    },
    id: {
      type: String,
      default: ''
    },
    closeCallback: Function
  },
  sockets: {
    '/socket/detail'(data) {
      if (data.sn !== this.sn || this.isFinished) return;

      try {
        this.updateData(data);
      } catch (error) {
        console.log(error);
      }
    }
  },
  data() {
    return {
      sampleLoading: false,
      resetLoading: false,
      loading: false,
      sampleStatus: -1,
      sensorOptions: makeSensorOpts(),
      channelOptions: makeChannelOpts(),
      sensorData: {},
      imsData: {},
      maxLen: 120,
      timing: {
        timer: null,
        visible: false,
        value: 0
      }
    };
  },
  computed: {
    step() {
      let collSampleBg, collSample, samplefilter, active;

      const dict = {
        '-1': ['等待采本底', '进样', '筛选', 0],
        0: ['等待采本底', '进样', '筛选', 0],
        1: ['采本底中', '进样', '筛选', 1],
        3: ['采本底完成', '等待进样', '筛选', 1],
        2: ['采本底完成', '进样中', '筛选', 2],
        4: ['采本底完成', '进样完成', '等待筛选', 2],
        5: ['采本底完成', '进样完成', '筛选完成', 3]
      };

      [collSampleBg, collSample, samplefilter, active] = dict[this.sampleStatus];

      return { collSampleBg, collSample, samplefilter, active };
    },
    title() {
      return this.$t('采样');
    },
    btnText() {
      return cfrBtnUtils.getStateText(this.sampleStatus);
    },
    isFinished() {
      return [4, 5].includes(this.sampleStatus);
    }
  },
  methods: {
    startTiming() {
      this.timing.visible = true;
      this.timing.start = Date.now();
      clearInterval(this.timing.timer);
      this.timing.timer = setInterval(() => {
        this.timing.value = Number((Date.now() - this.timing.start) / 1000).toFixed(0);
      }, 30);
    },
    addChartMark({ time, text, color }) {
      const data = {
        xAxis: time,
        label: {
          color,
          position: 'end',
          formatter: text
        },
        lineStyle: {
          color
        }
      };

      this.sensorOptions.series[0].markLine.data.push(data);
      this.channelOptions.series[0].markLine.data.push(data);
    },
    clearChartMark() {
      this.sensorOptions.series[0].markLine.data = [];
      this.channelOptions.series[0].markLine.data = [];
    },
    stopTiming() {
      this.timing.visible = false;
      clearInterval(this.timing.timer);
    },
    handleClose(done) {
      this.$destroy();
      isFunction(done) && done();
    },
    updateData(data) {
      const { imsData, sensorsData } = parseRealTimeChartData(data);
      this.sensorData = Object.assign({}, this.sensorData, sensorsData);
      this.imsData = Object.assign({}, this.imsData, imsData);

      const keys = Object.keys(this.sensorData);

      if (keys.length > this.maxLen) {
        const { sensors, ims } = keys.reduce(
          (map, e, i) => {
            if (i < keys.length - this.maxLen) return map;
            map.sensors[e] = this.sensorData[e];
            map.ims[e] = this.imsData[e];
            return map;
          },
          { sensors: {}, ims: {} }
        );

        this.sensorData = sensors;
        this.imsData = ims;
      }
    },
    setChannelChartData(data) {
      if (isEmpty(data)) return;

      this.channelOptions.dataset.source = data;
    },
    setSensorData(data) {
      if (isEmpty(data)) return;

      this.sensorOptions.dataset.source = data;
    },
    async getSampleList() {
      const status = this.sampleStatus;

      if (status == 0) return;

      this.loading = true;

      const type = sampleMap[status];

      const [err, res] = await awaitTo(
        getFcbrSampleData({
          sn: this.sn,
          id: this.id,
          sampleType: type
        })
      );

      this.loading = false;

      if (err) {
        console.log(err);
        return false;
      }

      const { imsData, sensorsData } = parseChartData(res.data.sampleData);

      this.setSensorData(sensorsData);
      this.setChannelChartData(imsData);

      return true;
    },
    async getSampleStatus() {
      const [err, res] = await awaitTo(getFcbrSampleStatus({ sn: this.sn, id: this.id }));

      if (err) {
        console.log(err);

        return false;
      }

      this.sampleStatus = res.data;

      return true;
    },
    async resetSample() {
      this.resetLoading = true;
      const [err] = await awaitTo(resetFcbrSampleById(this.id));

      this.resetLoading = false;

      if (err) return console.log(err);

      this.$message.success(this.$t('重置成功'));

      this.sensorData = {};
      this.imsData = {};

      this.sampleStatus = 0;
    },
    async setSample() {
      const { sampleType, type, text, color, afterStatus } = cfrBtnUtils.getOption(
        this.sampleStatus
      );

      if (isEmpty(sampleType) || isEmpty(type)) return;

      const [err, res] = await awaitTo(
        setFcbrSample({
          id: this.id,
          sn: this.sn,
          sampleType,
          type
        })
      );

      if (err) return console.log(err);

      if (res) {
        this.sampleStatus = afterStatus;

        if (afterStatus == 4) {
          this.clearChartMark();
          await this.getData(4000);
        } else {
          this.addChartMark({
            time: Date.now(),
            text,
            color
          });
        }
      }
    },
    async getData(t) {
      this.loading = true;

      const fn = async () => {
        await this.getSampleStatus();
        if (this.isFinished) {
          await this.getSampleList();
        }
      };

      if (isNumber(t)) {
        await new Promise((resolve, reject) => {
          setTimeout(async () => {
            try {
              await fn();
              resolve();
            } catch (error) {
              reject(error);
            }
          }, t);
        });
      } else {
        await fn();
      }

      this.loading = false;
    },
    async saveFcbrMatLibFilter() {
      const chart = this.$refs.charts2.chart;

      let zoomOpt;

      echarts.util.map(chart.getOption().dataZoom, rawOpt => {
        zoomOpt = rawOpt;
      });

      const [err] = await awaitTo(
        saveFcbrMatLibFilter({
          sn: this.sn,
          id: this.id,
          endTime: zoomOpt.endValue,
          startTime: zoomOpt.startValue
        })
      );

      if (err) {
        return console.err('保存筛选数据失败', err);
      }

      this.getData();
    },
    async dispatchEvent() {
      const dict = {
        0: { fn: this.setSample, hook: this.startTiming },
        3: { fn: this.setSample, hook: this.startTiming },
        1: { fn: this.setSample, hook: this.stopTiming },
        2: { fn: this.setSample, hook: this.stopTiming },
        4: {
          fn: this.saveFcbrMatLibFilter,
          hook() {
            this.stopTiming();
            this.handleZoom({ start: 0, end: 100 });
          }
        }
      };

      this.sampleLoading = true;

      const { fn, hook } = dict[this.sampleStatus];

      if (isFunction(fn)) {
        await fn.call(this);
        hook.call(this);
      }

      this.sampleLoading = false;
    },
    handleZoom(e) {
      this.channelOptions.dataZoom[0].start = e.start;
      this.channelOptions.dataZoom[0].end = e.end;

      this.sensorOptions.dataZoom[0].start = e.start;
      this.sensorOptions.dataZoom[0].end = e.end;
    }
  },
  mounted() {
    document.body.appendChild(this.$el);

    this.$nextTick(() => {
      this.getData();
    });
  },
  destroyed() {
    this.closeCallback?.();

    // if appendToBody is true, remove DOM node after destroy
    if (this.$el && this.$el.parentNode) {
      this.$el.parentNode.removeChild(this.$el);
    }
  },
  watch: {
    sensorData: {
      handler(n) {
        // this.sensorOptions.dataset.source = Object.values(n);
        this.sensorOptions.dataset.source = Object.values(n).sort((a, b) => a[0] - b[0]);
      },
      deep: true
    },
    imsData: {
      handler(n) {
        // this.channelOptions.dataset.source = Object.values(n);
        this.channelOptions.dataset.source = Object.values(n).sort((a, b) => a[0] - b[0]);
      },
      deep: true
    }
  }
};
</script>

<style lang="scss">
.cfr-sample-dialog {
  height: 100vh;
  overflow: hidden;
  box-sizing: border-box;

  &__header {
    height: 60px;
    display: flex;
    position: relative;

    .timing {
      --size: 46px;
      border-radius: 50%;
      border: 2px solid #45bcb9;
      width: var(--size);
      height: var(--size);
      text-align: center;
      line-height: var(--size);
      position: absolute;
      right: 60px;
      top: 0;
    }
  }

  &__main {
    height: calc(100% - 60px);
  }

  .el-dialog {
    height: 94vh;
    min-width: 1200px;

    &__body {
      height: calc(100% - 56px);
      box-sizing: border-box;

      .dialog-content {
        height: 97%;
        padding: 15px 20px;

        // @include scrollar-style-thin;

        overflow: hidden;
      }
    }
  }

  .el-steps {
    width: 400px;
    .el-step {
      .el-step__title {
        font-size: 14px;
      }

      .is-process {
        color: #0ef;
        border-color: #0ef;
      }

      .is-wait {
        color: #02757e;
        border-color: #02757e;
      }
    }
  }
}
</style>
